import { Mui, MuiIcons } from "@leap4/frontend";
import { sidebarColors } from "../../theme/colors";
import { ViewLensWordmark } from "./ViewLensWordmark";
import { SidebarNavigation } from "./SidebarNavigation";

const { Drawer, Box, IconButton } = Mui;
const { ChevronLeft, ChevronRight } = MuiIcons;

export const SIDEBAR_EXPANDED_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

// Same in both themes, as in the DSP: the sidebar is always dark.
const sidebarPaperSx = {
    bgcolor: sidebarColors.background,
    color: sidebarColors.text,
    borderRight: "none",
    "& .MuiListItemButton-root": {
        color: sidebarColors.text,
        "&:hover": { bgcolor: sidebarColors.selected },
        "&.Mui-selected, &.Mui-selected:hover": {
            bgcolor: sidebarColors.selected,
            color: "#fff",
            "& .MuiListItemIcon-root": { color: "#fff" },
        },
        "&.Mui-focusVisible": { outline: "2px solid #9589f5", outlineOffset: -2 },
    },
    "& .MuiListItemIcon-root, & .MuiIconButton-root, & .MuiSvgIcon-root": { color: sidebarColors.text },
    "& .Mui-selected .MuiSvgIcon-root": { color: "#fff" },
};

const Brand = () => (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minWidth: 0, py: 1 }}>
        <Box component="img" src="/adte-logo-mark.png" alt="ADTE" sx={{ height: 26, width: "auto", display: "block" }} />
        <ViewLensWordmark />
    </Box>
);

interface IAppShellSidebarProps {
    collapsed: boolean;
    onToggle: () => void;
    mobileOpen: boolean;
    onMobileClose: () => void;
    isMobile: boolean;
    width: number;
}

export const AppShellSidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose, isMobile, width }: IAppShellSidebarProps) => {
    const effectiveCollapsed = !isMobile && collapsed;
    const content = (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: effectiveCollapsed ? "center" : "space-between",
                    p: 1,
                    pl: effectiveCollapsed ? 1 : 2,
                    minHeight: 64,
                    gap: 1,
                }}
            >
                {!effectiveCollapsed && <Brand />}
                {!isMobile && (
                    <IconButton
                        onClick={onToggle}
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                        size="small"
                    >
                        {collapsed ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                )}
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                <SidebarNavigation collapsed={effectiveCollapsed} onNavigate={isMobile ? onMobileClose : undefined} />
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onMobileClose}
                ModalProps={{ keepMounted: true }}
                sx={{ "& .MuiDrawer-paper": { width: SIDEBAR_EXPANDED_WIDTH, maxWidth: "85vw", ...sidebarPaperSx } }}
            >
                {content}
            </Drawer>
        );
    }

    return (
        <Drawer
            variant="permanent"
            sx={{
                width,
                flexShrink: 0,
                "& .MuiDrawer-paper": { width, transition: "width 0.2s", overflowX: "hidden", ...sidebarPaperSx },
            }}
        >
            {content}
        </Drawer>
    );
};
